<script setup lang="ts">
//#region imports
import { computed, onMounted, ref, watch } from 'vue';
import hash from 'object-hash';
import { useI18n } from 'vue-i18n';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useFilterUtils } from '@/composables/useFilterUtils';
import type { GetAffiliatesData, GetAffiliatesResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type AffiliateId = string | Array<string> | null;
type ActivityDirection = 'from' | 'to';

export interface PlayerFilters {
  affiliate_id?: string[];
  player_country_code?: string[];
  belongs_to_affiliate?: boolean | string;
  deposits?: string;
  contacts?: string;
  activity?: [number | string, ActivityDirection?];
}
//#endregion

//#region define
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['submit']);
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();

const { apiToCustom, customToApi } = useFilterUtils({
  canBeUndefined: [
    'belongs_to_affiliate',
    'contacts',
  ],
});
//#endregion

//#region state
const currentFilters = ref<Omit<PlayerFilters, 'activity'> & { activity: [number | string, ActivityDirection?] }>({
  activity: [0],
});

// const currentFilters = ref<PlayerFilters>({
//   activity: [0],
// });
const isSelectLoading = ref(false);
const oldHash = ref<string | null>(null);
const newHash = ref<string | null>(null);

const affiliatesList = ref<GetAffiliatesResponse['payload']>([]);
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);

const isActiveButton = computed(() => {
  return oldHash.value !== newHash.value
    && !(isDisabledGroup.value
      && (currentFilters.value.deposits || currentFilters.value.contacts !== 'undefined' || currentFilters.value.activity?.[1]));
});

const hasFilters = computed(() => {
  return !_.isEmpty(props.modelValue);
});

const isDisabledGroup = computed(() => {
  return !currentFilters.value.affiliate_id?.length
    && !currentFilters.value.player_country_code?.length
    && currentFilters.value.belongs_to_affiliate === 'undefined';
});

const isPinnedAffiliatesFilter = computed(() => {
  return store.getters['auth/adminAcl'].is_superuser
    || (store.getters['auth/currentAcl'].players_available_properties
        .includes('affiliate_email')
      && store.getters['auth/currentAcl'].players_available_properties
        .includes('affiliate_id'));
});

const isAffiliatesFilter = computed(() => {
  return store.getters['auth/adminAcl'].is_superuser
    || (isPinnedAffiliatesFilter.value && store.getters['auth/currentAcl'].affiliates_view === 'allow');
});

const isPlayerCountryFilter = computed(() => {
  return store.getters['auth/adminAcl'].is_superuser
    || (store.getters['auth/currentAcl'].players_available_properties
        .includes('player_country_code')
      && store.getters['auth/currentAcl'].players_available_properties
        .includes('player_country_name'));
});

const isContactFilter = computed(() => {
  return store.getters['auth/adminAcl'].is_superuser
    || store.getters['auth/currentAcl'].players_available_properties
      .includes('email')
    || store.getters['auth/currentAcl'].players_available_properties
      .includes('phone_number');
});

const isLastActivityFilter = computed(() => {
  return store.getters['auth/adminAcl'].is_superuser
    || store.getters['auth/currentAcl'].players_available_properties
      .includes('last_active_at');
});

const depositOptions = computed(() => {
  return [
    {
      id: 'without',
      name: t('ui.filters.select.without'),
    },
    {
      id: 'first',
      name: t('ui.filters.select.only_first_dep'),
    },
    {
      id: 'more',
      name: t('ui.filters.select.more_one'),
    },
    {
      id: 'onlydep',
      name: t('ui.filters.select.only_with_dep'),
    },
  ];
});
//#endregion

//#region methods
function handleReset() {
  currentFilters.value = {
    activity: [0],
  };
  emit('submit', {});
}

function initFilters() {
  currentFilters.value = _.cloneDeep({
    activity: [0],
    ...apiToCustom(props.modelValue),
  });

  const affiliateId = currentFilters.value.affiliate_id as string | string[];

  if (affiliateId && affiliateId.length > 0) {
    getAffiliates(undefined, currentFilters.value.affiliate_id as AffiliateId);
  }

  oldHash.value = hash(currentFilters.value, { algorithm: 'md5' });
}

function handleApply() {
  const trimmed = _.mapValues(currentFilters.value, (v) =>
    typeof v === 'string' ? v.trim() : v,
  );

  const trimmedFilters = _.pickBy(trimmed, (v) =>
    !(Array.isArray(v) && v.length === 0),
  ) as PlayerFilters;

  if(!trimmedFilters.activity?.[1]) {
    delete trimmedFilters.activity;
    currentFilters.value.activity = [0];
  }

  const result = customToApi(trimmedFilters);
  emit('submit', result);
}

async function getAffiliates(searchValue: string | undefined, ids: AffiliateId) {
  isSelectLoading.value = true;

  let query = {
    search: searchValue || undefined,
    id: ids || undefined,
    limit: !ids ? 100 : undefined,
    account_status: 'approved',
  } as GetAffiliatesData['query'];

  try {
    const response = await api.getAffiliates(query);
    affiliatesList.value = response.data.payload;
  } catch (error) {
    console.error(error);
  } finally {
    isSelectLoading.value = false;
  }
}
//#endregion

//#region watch
watch(currentFilters, (newVal) => {
  newHash.value = hash(newVal, { algorithm: 'md5' });
}, { deep: true, immediate: true });
//#endregion

//#region hooks
initFilters();

onMounted(() => {
  if (
    currentFilters.value
    && (
      (currentFilters.value.affiliate_id && !isAffiliatesFilter.value)
      || (currentFilters.value.player_country_code && !isPlayerCountryFilter.value)
      || (currentFilters.value.belongs_to_affiliate && !isPinnedAffiliatesFilter.value)
      || (currentFilters.value.activity && !isLastActivityFilter.value)
    )
  ) {
    handleReset();
  }
});
//#endregion


</script>

<template>
  <UiFilterElement
    class="player-filter"
    :is-active-button="isActiveButton"
    :has-filters="hasFilters"
    @open="initFilters"
    @apply="handleApply"
    @reset="handleReset"
  >
    <UiFilterItem
      v-if="isAffiliatesFilter"
      label="ui.filters.affiliate"
    >
      <UiSelectElement
        v-model="currentFilters.affiliate_id"
        :placeholder="$t('ui.filters.select.all')"
        :list="affiliatesList"
        :remote-method="getAffiliates"
        :loading="isSelectLoading"
        :max-collapse-tags="1"
        width="200"
        option-name-param="email"
        option-label-param="email"
        remote
        collapse-tag-tooltip
        multiple
        ellipsis
        filterable
        clearable
      />
    </UiFilterItem>
    <UiFilterItem
      v-if="isPlayerCountryFilter"
      label="ui.filters.playerCountryNameFilter"
    >
      <UiSelectElement
        v-model="currentFilters.player_country_code"
        :placeholder="$t('ui.filters.select.all')"
        :list="countries"
        width="200"
        search-param="name"
        option-value-param="code"
        filterable
        multiple
        collapse-tag-tooltip
        :max-collapse-tags="1"
      />
    </UiFilterItem>
    <UiFilterItem
      v-if="isAffiliatesFilter"
      label="ui.filters.pinnedAffiliate"
    >
      <UiRadioGroup
        v-model="currentFilters.belongs_to_affiliate"
        :options="[
          {
            label: $t('ui.filters.select.all'),
            value: 'undefined',
          },
          {
            label: $t('ui.filters.select.no'),
            value: false,
          },
          {
            label: $t('ui.filters.select.yes'),
            value: true,
          }
        ]"
      />
    </UiFilterItem>
    <UiFilterItem
      label="ui.filters.deposits"
      :disabled="isDisabledGroup"
    >
      <UiSelectElement
        v-model="currentFilters.deposits"
        :placeholder="$t('ui.filters.select.all')"
        :list="depositOptions"
        width="200"
        filterable
        clearable
      />
    </UiFilterItem>
    <UiFilterItem
      v-if="isContactFilter"
      label="ui.filters.contacts"
      :disabled="isDisabledGroup"
    >
      <UiRadioGroup
        v-model="currentFilters.contacts"
        :options="[
          {
            label: $t('ui.filters.select.all'),
            value: 'undefined',
          },
          {
            label: $t('ui.filters.select.only_content'),
            value: 'only',
          }
        ]"
      />
    </UiFilterItem>
    <UiFilterItem
      v-if="isLastActivityFilter"
      label="ui.filters.lastActivity"
      :disabled="isDisabledGroup"
    >
      <div class="t:flex t:items-center t:space-x-16">
        <UiRadioButton
          v-model="currentFilters.activity[1]"
          :text="$t('ui.filters.select.all')"
        />
        <div class="t:flex t:items-center t:space-x-8">
          <UiTabs
            v-model="currentFilters.activity[1]"
            :list="[
              {
                value: 'from',
                name: '&lt;',
              },
              {
                value: 'to',
                name: '&gt;',
              },
            ]"
          />
          <UiInput
            v-model="currentFilters.activity[0]"
            :disabled="!currentFilters.activity?.[1]"
            :width="72"
          />
          <span>{{ $t('ui.filters.inner.days') }}</span>
        </div>
      </div>
    </UiFilterItem>
  </UiFilterElement>
</template>
